import { EditorSimpleSelect } from "./editor-simple-select";
import { Feature, MultiPolygon, Point, Polygon, Position } from "geojson";
import { GeometryUtil } from "../../geometry";
import { VueMapPopup } from "../../control/VueMapPopup";
import { defineAsyncComponent } from "vue";
import { FeatureUtil } from "../../feature";
import { DrawFeature } from "@mapbox/mapbox-gl-draw";
import { InteractionState } from "../abstract-interaction";
import { ViewSimpleSelect } from "../view/view-simple-select";
import { Layer } from "../../../document/model/layer";
import { RendererUtils } from "../../renderer/renderer-utils";
import { DynamicDialogCloseOptions } from "primevue/dynamicdialogoptions";
import MapLayerSelectionDialog from "../../../../components/map/MapLayerSelectionDialog.vue"
import store from "../../../../js/store";
import {mapboxglref} from "@/js/mapboxService";

let MapInputNumberMarker = defineAsyncComponent(() => import("../../../../components/map/markers/MapInputNumber.vue"));

export class BufferInteractionState extends InteractionState {
  radius: number;
}

export class EditorBuffer<T extends BufferInteractionState> extends ViewSimpleSelect<T> {
  public mapboxgl = mapboxglref();
  private bufferPolygon: DrawFeature | undefined;
  private radiusInput: VueMapPopup<typeof MapInputNumberMarker> | undefined;

  buildInitialState(options: any): any {
    let state = super.buildInitialState(options);
    let radiusInputOptions: any = {
      initialValue: 1,
      step: 1,
      styleclass: 'map-input-number-lg',
      suffix: ' m',
    };
    if (['Polygon', 'MultiPolygon'].indexOf(state.getFeature().geometry.type) == -1) {
      radiusInputOptions.min = 1;
    }
    this.radiusInput = new VueMapPopup<typeof MapInputNumberMarker>(MapInputNumberMarker, {
      ...radiusInputOptions,
      onInput: (value: number) => {
        this.bufferPolygon = this.createFeature(
            FeatureUtil.buffer(state.features.get(state.featureID), value)
        );
        this.bufferPolygon.id = 'buffer';
        this.displayDrawFeature(this.bufferPolygon);

        if (!state.isDirty) {
          this.controller.interaction.historyPush(state);
        }
        this.tick();
      }
    })
    this.radiusInput.addTo(this.controller.mapInstance);
    let center = GeometryUtil.center(state.features.get(state.featureID));

    this.radiusInput.setLngLat(new this.mapboxgl.LngLat(center[0], center[1]));

    return state;
  }

  onStop(state: T) {
    super.onStop(state);
    this.radiusInput.remove();
    this.radiusInput = undefined;

    if (this.bufferPolygon) {
      this.removeDrawFeature(this.bufferPolygon.id.toString());
      this.bufferPolygon = undefined;
    }
  }


  canSave(state: T): boolean {
    return this.controller.interaction.hasHistory();
  }

  save(state: T, nextInteraction: string | undefined = undefined) {
    let feature = GeometryUtil.polygon((this.bufferPolygon.getCoordinates() as Position[][])[0]);
    this.controller.configuration.dialogProvider().open(
        MapLayerSelectionDialog,
        {
          props: {
            header: this.controller.configuration.translatorProvider('Export to existing layer'),
            style: {
              "width": "500px",
              "min-width": '200px',
              "min-height": "200px",
              "resize": "both",
              "overflow": "hidden"
            }
          },
          data: {
            mapController: this.controller,
            compatibilityFn: (layer: Layer) => {
              return [
                    RendererUtils.DocumentTypes.VectorTiles,
                    RendererUtils.DocumentTypes.Vector,
                  ].indexOf(layer.documentCategory.documentCategoryId as number) >= 0
                  && layer.properties?.shapeType?.shapeTypeId === 1;
            }
          },
          onClose: (options: DynamicDialogCloseOptions) => {
            if (options.data?.sourceLayer === undefined) {
              return;
            }

            let layerGeometry = {
              documentId: options.data?.sourceLayer?.documentId,
              geometry: feature.geometry,
              attributes: {}
            }

            store.dispatch('saveBulkGeometries', {
              data: [layerGeometry],
              headers: {
                'x-access-token': this.controller.configuration.accessToken
              }
            }).then(response => {
              this.controller.configuration.toastProvider({
                severity: "success",
                summary: this.controller.configuration.translatorProvider("Done"),
                detail: this.controller.configuration.translatorProvider('Successfully saved!'),
                life: 5000,
              });
              if (this.controller.hasLayer(options.data?.sourceLayer)) {
                this.controller.reloadLayer(options.data?.sourceLayer);
              }
              this.controller.interaction.historyReset();
              this.controller.interaction.mode.resetInteraction();
            }).catch(error => {
              this.controller.configuration.toastProvider({
                severity: "error",
                summary: "Error",
                detail: this.controller.configuration.translatorProvider(error),
                life: 5000,
              });
            })
          }
        }
    );
  }
}
